import React, { useMemo, useEffect, useState } from 'react';
import { Dropdown, Table, Tag, Button, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import { useAgentSelector } from '../../../hooks/useAgentStore';
import {
  ChecklistItem,
  ChecklistItemStatuses,
  Result,
  RowPosition,
} from '../../../interfaces/changeRequest';
import NoDataFound from '../../NoDataFound/NoDataFound';
import SanitizedMarkdown from '../../SanitizedMarkdown/SanitizedMarkdown';
import ErrorResult from '../../ErrorResult/ErrorResult';
import {
  CheckCircleOutlined,
  HolderOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { ReactComponent as AISvg } from '../../../assets/ai.svg';
import EditChangeRequestAnalysis from '../EditChangeRequest/EditChangeRequestAnalysis/EditChangeRequestAnalysis';
import { useChangeRequest } from '../../../hooks/useChangeRequest';
import { ChangeRequestSource } from '../../../enums/changeRequest';
import { setCopilotChatSessionId } from '../../../services/copilotThread.service';
import { SerializableError } from '../../../utils/errorHandling';
import { ItemType } from 'antd/es/menu/interface';
import * as subscriptionService from '../../../services/subscription';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DraggableRow, { DraggableRowProps } from '../DraggableRow/DraggableRow';
import ResizableTitle from '../ResizableTitle/ResizableTitle';
import useResizableColumns from '../../../hooks/useResizableColumns';
import { isSameRecord } from '../../../utils/tableUtils';
import { isOwnChangeRequest } from '../../../services/changeRequestService';
import { NOT_OWN_CHANGE_REQUEST } from '../../../constants/texts';
import { selectSubscriptionStatus } from '../../../app/Slices/subscriptionSlice';
interface ChangeRequestAnalysisProps {
  isPrintView?: boolean;
}
const ChangeRequestAnalysis: React.FC<ChangeRequestAnalysisProps> = ({
  isPrintView = false,
}) => {
  const [dataSource, setDataSource] = useState<ChecklistItem[]>([]);

  // Global store for shared state
  const org = useSelector((state: RootState) => state.organization.org);
  const status = useSelector(selectSubscriptionStatus);

  // Agent store for change request state
  const { data, error, loading, source } = useAgentSelector(
    (state) => state.changeRequest
  );
  const { currentStep } = useAgentSelector((state) => state.changeRequestSteps);

  const cr = useMemo(() => {
    return data?.result;
  }, [data?.result]);

  useEffect(() => {
    setDataSource(cr?.checklist?.items ?? []);
  }, [cr]);
  const isUserReadonly = subscriptionService.isUserReadonly(status);
  const isCrReadOnly = !cr || !isOwnChangeRequest(org, cr);
  const isReadonly = isCrReadOnly || isUserReadonly;
  const readonlyMsg = isCrReadOnly
    ? NOT_OWN_CHANGE_REQUEST
    : subscriptionService.getReadonlyElementTitle();

  const isSummaryStep = currentStep === 2 || isPrintView;

  const {
    editingItem,
    isDirty,
    setIsDirty,
    navigateToEdit,
    handleEditRow,
    handleDeleteRow,
    updateDataArray,
    handleAddRow,
    saveItem,
    tblRef,
    handleExpandToFullScreen,
  } = useChangeRequest<ChecklistItem>(
    cr?.checklist?.items,
    source,
    loading,
    ChangeRequestSource.EDIT_ANALYSIS_CHECKLIST,
    '/checklist'
  );
  const [rowIndex, setRowIndex] = useState(0);

  useEffect(() => {
    // Setting copilot info for analysis checklist
    setCopilotChatSessionId('', 2);
  }, [editingItem]);

  const sourceApplicable = useMemo(
    () =>
      !source ||
      source === ChangeRequestSource.ANALYSIS_CHECKLIST ||
      source === ChangeRequestSource.EDIT_ANALYSIS_CHECKLIST,
    [source]
  );
  const _loading = sourceApplicable && loading;
  const _error = (sourceApplicable && error) as SerializableError | false;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const addRowTemplate = {
    id: `temp-${Date.now()}`,
    item: 'New Item',
    question: '',
    answer: '',
    status: 'draft' as ChecklistItemStatuses,
  };

  const dropdownMenu = (record: ChecklistItem): ItemType[] => [
    {
      label: (
        <Tooltip title={readonlyMsg} placement="right" destroyOnHidden={false}>
          <span className="w-100">Clear warnings</span>
        </Tooltip>
      ),
      key: 'clear-warnings',
      disabled: isReadonly || !record.warnings?.length,
      onClick: async (info) => {
        info.domEvent.stopPropagation();
        const updatedItem = { ...record, warnings: undefined };
        await saveItem(updatedItem);
      },
    },
    {
      label: (
        <Tooltip title={readonlyMsg} placement="right" destroyOnHidden={false}>
          <span className="w-100">Edit row</span>
        </Tooltip>
      ),
      key: 'edit',
      disabled: isReadonly,
      onClick: (info) => {
        info.domEvent.stopPropagation();
        setRowIndex(rowIndex);
        handleEditRow(record);
      },
    },
    {
      label: (
        <Tooltip title={readonlyMsg} placement="right" destroyOnHidden={false}>
          <span className="w-100">Delete row</span>
        </Tooltip>
      ),
      key: 'delete',
      disabled: isReadonly,
      onClick: (info) => {
        info.domEvent.stopPropagation();
        handleDeleteRow(record);
      },
    },
    {
      label: (
        <Tooltip title={readonlyMsg} placement="right" destroyOnHidden={false}>
          <span className="w-100">Add row above</span>
        </Tooltip>
      ),
      key: 'add-above',
      disabled: isReadonly,
      onClick: (info) => {
        info.domEvent.stopPropagation();
        handleAddRow(record, RowPosition.ABOVE, { ...addRowTemplate });
      },
    },
    {
      label: (
        <Tooltip title={readonlyMsg} placement="right" destroyOnHidden={false}>
          <span className="w-100">Add row below</span>
        </Tooltip>
      ),
      key: 'add-below',
      disabled: isReadonly,
      onClick: (info) => {
        info.domEvent.stopPropagation();
        handleAddRow(record, RowPosition.BELOW, { ...addRowTemplate });
      },
    },
  ];

  const columns: ColumnsType<ChecklistItem> = useMemo(() => {
    const totalWidth = 1200;
    const fixedWidth = (75 / totalWidth) * totalWidth;
    const remainingWidth = totalWidth - fixedWidth * 2;
    const analysisColumns: ColumnsType<ChecklistItem> = [
      {
        title: 'S No.',
        dataIndex: 'id',
        key: 'sno',
        width: fixedWidth,
        render: (_value, _record, index) => {
          return (
            <div className="dnd-handle-icon">
              <HolderOutlined />
              {index + 1}
            </div>
          );
        },
      },
      {
        title: 'Item',
        dataIndex: 'item',
        key: 'item',
        width: (20 / 90) * remainingWidth,
      },
      {
        title: 'Question',
        dataIndex: 'question',
        key: 'question',
        width: (27.5 / 90) * remainingWidth,
        render: (question: string) => (
          <SanitizedMarkdown inputSrc={question} isPrintView={isPrintView} />
        ),
      },
      {
        title: 'Answer',
        dataIndex: 'answer',
        key: 'answer',
        width: (27.5 / 90) * remainingWidth,
        render: (answer: string, record: ChecklistItem) => {
          const warningsList =
            typeof record.warnings === 'string'
              ? [record.warnings]
              : Array.isArray(record.warnings)
                ? record.warnings
                : [];
          const hasWarnings = warningsList.length > 0;

          return (
            <div>
              {hasWarnings && (
                <div style={{ color: 'var(--orange-6)', marginBottom: '8px' }}>
                  {warningsList.map((warning: string, index: number) => (
                    <div key={index}>{warning}</div>
                  ))}
                </div>
              )}
              <SanitizedMarkdown inputSrc={answer} isPrintView={isPrintView} />
            </div>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: (10 / 90) * remainingWidth,
        // Add status filter only if it is not the summary step.
        ...(!isSummaryStep
          ? {
              filters: [
                { text: 'Draft', value: 'draft' },
                { text: 'Reviewed', value: 'reviewed' },
                { text: 'Warning', value: 'warning' },
              ],
              onFilter: (value, record) => {
                if (value === 'warning') {
                  const warningsList =
                    typeof record.warnings === 'string'
                      ? [record.warnings]
                      : Array.isArray(record.warnings)
                        ? record.warnings
                        : [];
                  return warningsList.length > 0;
                }
                return record.status === value;
              },
            }
          : {}),
        render: (status: ChecklistItemStatuses, record: ChecklistItem) => {
          const warningsList =
            typeof record.warnings === 'string'
              ? [record.warnings]
              : Array.isArray(record.warnings)
                ? record.warnings
                : [];
          const hasWarnings = warningsList.length > 0;

          let color = status === 'reviewed' ? 'green' : 'blue';
          let text = status.toUpperCase();

          if (hasWarnings) {
            color = 'orange';
            text = 'WARNING';
          }

          return (
            <Tag color={color}>
              {status === 'reviewed' && !hasWarnings && <CheckCircleOutlined />}
              <span>{text}</span>
            </Tag>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: fixedWidth,
        hidden: isSummaryStep,
        render: (_, record, index) => (
          <div className="action-items">
            <Tooltip
              title={isReadonly ? readonlyMsg : 'Ask Cirra AI'}
              destroyOnHidden={false}
            >
              <Button
                type="text"
                disabled={!org?.active || isReadonly}
                icon={
                  <AISvg className={org?.active ? undefined : 'disabled'} />
                }
                onClick={
                  org?.active
                    ? (e) => {
                        e.stopPropagation(); // Prevent triggering row click
                        handleEditRow(record);
                      }
                    : undefined
                }
              />
            </Tooltip>

            <Dropdown
              menu={{
                items: dropdownMenu(record),
              }}
              trigger={['click']}
            >
              <MoreOutlined
                className="dots-icon"
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </div>
        ),
      },
    ];
    return analysisColumns;
  }, [dropdownMenu, currentStep, cr, isPrintView]);

  const resizableColumns = useResizableColumns(columns, 150);

  if ((!cr || cr?.checklist?.items?.length === 0) && !_loading && !_error) {
    return (
      <NoDataFound description="Enter your request details and click 'Analyze Request' to create a detailed checklist" />
    );
  }

  if (_error && !_loading) {
    return <ErrorResult code={_error?.statusCode} subTitle={_error?.message} />;
  }

  const expandToFullScreen = () => {
    if (editingItem) {
      handleExpandToFullScreen(editingItem);
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.id === over?.id
        );
        const updatedDataSource = arrayMove(prevState, activeIndex, overIndex);
        updateDataArray({ items: updatedDataSource });
        return updatedDataSource;
      });
    }
  };

  return (
    <>
      {editingItem && (
        <EditChangeRequestAnalysis
          editingRecord={editingItem}
          onClose={() => handleEditRow(null)}
          onNavigate={navigateToEdit}
          onSave={async (updatedItem) => {
            const result = await saveItem(updatedItem);
            return result as Result;
          }}
          itemCount={cr?.checklist?.items.length || 0}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          expandToFullScreen={expandToFullScreen}
        />
      )}
      <div id="change-request-analysis" data-testid="change-request-analysis">
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={dataSource.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              ref={tblRef}
              columns={resizableColumns}
              rowKey={(record) => record.id}
              loading={_loading}
              className={`${_loading ? 'table-spinner' : ''} ${
                editingItem && !_loading ? 'editing-item-table' : ''
              } ${isSummaryStep || !!editingItem ? '' : 'dnd-table'}`}
              dataSource={dataSource || []}
              tableLayout="auto"
              scroll={{ x: isPrintView ? '100%' : 1200 }}
              style={{ width: '100%' }}
              pagination={false}
              rowClassName={(record) =>
                isSameRecord(record, editingItem) ? 'active-row' : ''
              }
              onRow={(record) => {
                return {
                  onClick: () => {
                    editingItem && handleEditRow(record);
                  },
                };
              }}
              components={{
                header: { cell: ResizableTitle },
                body: {
                  row: (props: DraggableRowProps) => (
                    <DraggableRow
                      {...props}
                      isDnDDisabled={isSummaryStep || !!editingItem}
                    />
                  ),
                },
              }}
            />
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
};

export default ChangeRequestAnalysis;
